<template>
  <lila-default-template>
    <template #mainContent>
      <div class="container team-page">
        <!-- POC Cuidados Paliativos (Tela 8): detalhe de uma equipe.
             Aberta só a partir do painel "Rede de CP" (Tela 7). -->
        <router-link
          :to="{ name: 'palliativeNetwork', query: $route.query.de === 'matriciamento' ? { visao: 'matriciamento' } : {} }"
          class="td-back"
        >
          ← Rede de CP
        </router-link>

        <p
          v-if="!team"
          class="td-empty"
        >
          {{ loading ? "Carregando…" : loadError || "Equipe não encontrada." }}
        </p>

        <template v-else>
          <div class="td-header">
            <div>
              <span class="td-type">{{ team.type === "matricial" ? "Equipe matricial" : "Equipe assistencial" }}</span>
              <h1 class="td-name">
                {{ team.name }}
              </h1>
              <p class="td-sub">
                {{ team.place }}
              </p>
            </div>
          </div>

          <!-- Aviso de renovação da habilitação -->
          <div
            class="td-card td-renewal"
            :class="`is-${renewal.tone}`"
          >
            <span
              class="td-status"
              :class="`is-${renewal.tone}`"
            >
              <span aria-hidden="true">{{ statusIcon(renewal.tone) }}</span>
              <template v-if="team.renewalInMonths == null">
                Data de renovação da habilitação não informada
              </template>
              <template v-else>
                Renovação da habilitação em {{ team.renewalInMonths }} {{ team.renewalInMonths === 1 ? "mês" : "meses" }}
              </template>
            </span>
            <p class="td-renewal-text">
              {{ renewal.text }}
            </p>
          </div>

          <!-- Composição mínima x cadastrada -->
          <section class="td-section">
            <h2 class="td-section-title">
              Composição mínima x cadastrada
              <span class="td-section-sub">exigência da PNCP para equipe {{ team.type }}</span>
            </h2>

            <div class="td-card td-table-card">
              <table class="td-table">
                <thead>
                  <tr>
                    <th>Categoria</th>
                    <th class="is-number">
                      Exigido
                    </th>
                    <th>Cadastrado</th>
                    <th class="is-number">
                      Carga horária
                    </th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in composition"
                    :key="row.category"
                  >
                    <td class="td-strong">
                      {{ row.category }}
                    </td>
                    <td class="is-number">
                      {{ row.required }}h
                    </td>
                    <td>
                      <span v-if="row.names.length > 0">{{ row.names.join(", ") }}</span>
                      <span
                        v-else
                        class="td-muted"
                      >Ninguém cadastrado</span>
                    </td>
                    <td class="is-number">
                      {{ row.hours }}h
                    </td>
                    <td>
                      <span
                        class="td-status"
                        :class="`is-${row.status.tone}`"
                      >
                        <span aria-hidden="true">{{ statusIcon(row.status.tone) }}</span>
                        {{ row.status.label }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Produção -->
          <section class="td-section">
            <h2 class="td-section-title">
              Produção
              <span class="td-section-sub">últimos 6 meses</span>
            </h2>

            <div class="td-two-cols">
              <!-- Produção mensal x meta -->
              <div class="td-card">
                <header class="td-chart-header">
                  <p class="td-chart-headline td-goal-headline">
                    <strong>{{ currentProduction.value }}</strong> procedimentos em {{ currentMonthName }}
                  </p>
                  <span
                    class="td-status"
                    :class="currentProduction.value >= goal ? 'is-good' : 'is-critical'"
                  >
                    <span aria-hidden="true">{{ currentProduction.value >= goal ? "✓" : "!!" }}</span>
                    {{ currentProduction.value >= goal ? "Meta atingida" : "Abaixo do mínimo" }}
                  </span>
                </header>

                <div
                  class="td-bars"
                  role="img"
                  :aria-label="productionAriaLabel"
                >
                  <div
                    class="td-goal-line"
                    :style="{ bottom: `${pctOfMax(goal)}%` }"
                  >
                    <span>Meta mensal: {{ goal }}</span>
                  </div>
                  <div
                    v-for="(item, index) in team.production"
                    :key="item.month"
                    class="td-bar-slot"
                    @mouseenter="hoverBar = index"
                    @mouseleave="hoverBar = null"
                  >
                    <span
                      v-if="hoverBar === index || index === team.production.length - 1"
                      class="td-bar-label"
                      :style="{ bottom: `calc(${pctOfMax(item.value)}% + 6px)` }"
                    >{{ item.value }}</span>
                    <div
                      class="td-bar"
                      :class="{ 'is-hover': hoverBar === index, 'is-below': item.value < goal }"
                      :style="{ height: `${pctOfMax(item.value)}%` }"
                    />
                  </div>
                </div>
                <div class="td-bar-axis">
                  <span
                    v-for="item in team.production"
                    :key="item.month"
                  >{{ item.month }}</span>
                </div>
                <p class="td-chart-note">
                  Barras abaixo da linha tracejada: mês com o repasse suspenso.
                </p>
              </div>

              <!-- Atividades do Art. 35 -->
              <div class="td-card">
                <header class="td-chart-header">
                  <p class="td-chart-headline">
                    Atividades avaliadas no mês
                  </p>
                  <span class="td-chart-legend">Art. 35 da PNCP</span>
                </header>

                <ul
                  class="td-activities"
                  role="list"
                >
                  <li
                    v-for="item in activities"
                    :key="item._id"
                    class="td-activity"
                  >
                    <span class="td-activity-name">{{ item.name }}</span>
                    <span
                      class="td-activity-track"
                      aria-hidden="true"
                    >
                      <span
                        class="td-activity-fill"
                        :style="{ width: `${activityPct(item.value)}%` }"
                      />
                    </span>
                    <span
                      class="td-activity-value"
                      :class="{ 'is-zero': !item.value }"
                    >{{ item.value == null ? "—" : item.value }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <!-- Certificação em CP -->
          <section class="td-section">
            <h2 class="td-section-title">
              Certificação em cuidados paliativos
              <span class="td-section-sub">profissionais de nível superior</span>
            </h2>

            <div class="td-card td-table-card">
              <table class="td-table">
                <thead>
                  <tr>
                    <th>Profissional</th>
                    <th>Categoria</th>
                    <th>Certificado</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="member in certifications"
                    :key="member.id"
                  >
                    <td class="td-strong">
                      {{ member.name }}
                    </td>
                    <td>{{ member.category }}</td>
                    <td>{{ certificateLabel(member.certificate) }}</td>
                    <td>
                      <span
                        class="td-status"
                        :class="`is-${member.status.tone}`"
                      >
                        <span aria-hidden="true">{{ statusIcon(member.status.tone) }}</span>
                        {{ member.status.label }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p class="td-chart-note">
                Para renovar a habilitação, todo profissional de nível superior precisa de aperfeiçoamento (180h),
                especialização (360h) ou trilhas UNA-SUS somando 180h.
              </p>
            </div>
          </section>
        </template>
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import moment from "moment";
import DefaultTemplate from "@components/template/DefaultTemplate.vue";
import {
  ART35_ACTIVITIES,
  HIGHER_EDUCATION,
  REQUIRED_COMPOSITION,
  getTeamDetail
} from "@/services/palliative/networkApi";
import { PNCP_MINIMUM_PER_MONTH, currentCompetence } from "@/services/palliative/susProductionApi";

const CERTIFICATE_MIN_HOURS = 180;

export default {
  name: "PalliativeTeamDetail",
  components: {
    "lila-default-template": DefaultTemplate
  },
  data() {
    return {
      goal: PNCP_MINIMUM_PER_MONTH,
      team: null,
      loading: true,
      loadError: "",
      hoverBar: null
    };
  },
  computed: {
    // Uma linha por categoria exigida, somando a carga horária dos cadastrados.
    composition() {
      return this.team.required.map((req) => {
        const members = this.team.members.filter((m) => m.category === req.category);
        const hours = members.reduce((sum, m) => sum + m.hours, 0);
        let status = { tone: "good", label: "Completo" };
        if (members.length === 0) status = { tone: "critical", label: "Vaga em aberto" };
        else if (hours < req.hours) status = { tone: "warning", label: "Carga horária abaixo" };
        return { category: req.category, required: req.hours, names: members.map((m) => m.name), hours, status };
      });
    },
    certifications() {
      return this.team.members
        .filter((m) => HIGHER_EDUCATION.includes(m.category))
        .map((m) => ({ ...m, status: this.certificateStatus(m.certificate) }))
        .sort((a, b) => this.toneRank(b.status.tone) - this.toneRank(a.status.tone));
    },
    renewal() {
      const missing = this.certifications.filter((m) => m.status.tone === "critical").length;
      const inProgress = this.certifications.filter((m) => m.status.tone === "warning").length;
      const openSeats = this.composition.filter((r) => r.status.tone === "critical").length;

      if (missing + inProgress + openSeats === 0) {
        return { tone: "good", text: "Composição completa e todos os profissionais de nível superior certificados." };
      }

      const parts = [];
      if (openSeats) parts.push(`${openSeats} ${openSeats === 1 ? "vaga em aberto" : "vagas em aberto"}`);
      if (missing) parts.push(`${missing} ${missing === 1 ? "profissional sem certificado" : "profissionais sem certificado"}`);
      if (inProgress) parts.push(`${inProgress} com certificação em andamento`);
      return {
        tone: this.team.renewalInMonths != null && this.team.renewalInMonths <= 6 ? "critical" : "warning",
        text: `Pendências para a renovação: ${parts.join(", ")}.`
      };
    },
    // Atividades do art. 35, da maior para a menor. Sem fonte na plataforma = "—".
    activities() {
      return ART35_ACTIVITIES
        .map((a) => ({ ...a, value: this.team.art35[a.field] }))
        .sort((a, b) => (b.value || 0) - (a.value || 0));
    },
    activityMax() {
      return Math.max(1, ...this.activities.map((a) => a.value || 0));
    },
    currentMonthName() {
      return currentCompetence().label.split("/")[0].toLowerCase();
    },
    currentProduction() {
      return this.team.production[this.team.production.length - 1] || { month: "", value: 0 };
    },
    chartMax() {
      const values = this.team.production.map((p) => p.value);
      return Math.ceil((Math.max(this.goal, ...values) * 1.15) / 10) * 10;
    },
    productionAriaLabel() {
      const values = this.team.production.map((p) => `${p.month}: ${p.value}`).join(", ");
      return `Procedimentos por mês contra a meta de ${this.goal}. ${values}.`;
    }
  },
  watch: {
    "$route.params.id"(id) {
      if (id) this.load();
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const detail = await getTeamDetail(this.$route.params.id);
        const type = detail.team.type === "matricial" ? "matricial" : "assistencial";
        this.team = {
          id: detail.team.id,
          type,
          name: detail.team.name,
          place: detail.team.municipality,
          renewalInMonths: detail.team.renewalDate
            ? Math.max(0, moment(detail.team.renewalDate, "YYYY-MM-DD").diff(moment().startOf("day"), "months"))
            : null,
          members: detail.members.map((m) => ({
            id: m.id,
            name: m.name,
            category: m.category || "Sem CBO",
            hours: Number(m.weeklyHours) || 0,
            certificate: m.certificateType ? { type: m.certificateType, hours: m.certificateHours || 0 } : null,
            warnings: m.warnings
          })),
          production: detail.production,
          art35: detail.art35,
          required: REQUIRED_COMPOSITION[type]
        };
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar a equipe.", error);
        this.team = null;
        this.loadError = error.status === 404 ? "" : error.message;
      } finally {
        this.loading = false;
      }
    },
    certificateStatus(certificate) {
      if (!certificate) return { tone: "critical", label: "Sem certificado" };
      if (certificate.type === "unasus" && certificate.hours < CERTIFICATE_MIN_HOURS) {
        return { tone: "warning", label: `Em andamento: ${certificate.hours} de ${CERTIFICATE_MIN_HOURS}h` };
      }
      return { tone: "good", label: "Válido" };
    },
    certificateLabel(certificate) {
      if (!certificate) return "—";
      if (certificate.type === "especializacao") return `Especialização (${certificate.hours}h)`;
      if (certificate.type === "aperfeicoamento") return `Aperfeiçoamento (${certificate.hours}h)`;
      return `Trilhas UNA-SUS (${certificate.hours}h)`;
    },
    toneRank(tone) {
      return { critical: 2, warning: 1, good: 0 }[tone] ?? 0;
    },
    statusIcon(tone) {
      return { critical: "!!", warning: "!", good: "✓" }[tone] || "";
    },
    pctOfMax(value) {
      return (value / this.chartMax) * 100;
    },
    activityPct(value) {
      return ((value || 0) / this.activityMax) * 100;
    }
  }
};
</script>

<style lang="scss" scoped>
$ink: #2b2b3a;
$ink-muted: #6b6b7b;
$primary: #4f3f82;
$primary-soft: #ece9f5;
$grid: #e4e2ea;

$critical: #c0262d;
$critical-soft: #fdecec;
$warning: #9a5b00;
$warning-soft: #fff4e0;
$good: #2e7d32;
$good-soft: #e8f5e9;

.team-page {
  padding-top: 30px;
  padding-bottom: 40px;
  color: $ink;
}

.td-back {
  display: inline-block;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  color: $primary;
}

.td-empty {
  font-size: 14px;
  color: $ink-muted;
}

.td-card {
  padding: 20px 25px;
  border-radius: 15px;
  background-color: #ffffff;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
}

/* Cabeçalho */
.td-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.td-type {
  display: inline-block;
  margin-bottom: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: $primary;
  background-color: $primary-soft;
}

.td-name {
  margin: 0;
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
  color: $primary;
}

.td-sub {
  font-size: 14px;
  color: $ink-muted;
}

.td-mock-badge {
  padding: 4px 12px;
  border: 1px dashed $ink-muted;
  border-radius: 999px;
  font-size: 12px;
  color: $ink-muted;
  white-space: nowrap;
}

/* Renovação */
.td-renewal {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-bottom: 32px;
  padding: 14px 20px;
  border-left: 4px solid $good;

  &.is-warning {
    border-left-color: $warning;
  }

  &.is-critical {
    border-left-color: $critical;
  }
}

.td-renewal-text {
  font-size: 14px;
}

/* Seções */
.td-section {
  margin-bottom: 32px;
}

.td-section-title {
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 700;
}

.td-section-sub {
  margin-left: 6px;
  font-size: 13px;
  font-weight: 400;
  color: $ink-muted;
}

/* Status: sempre ícone + rótulo */
.td-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;

  &.is-good {
    color: $good;
    background-color: $good-soft;
  }

  &.is-warning {
    color: $warning;
    background-color: $warning-soft;
  }

  &.is-critical {
    color: $critical;
    background-color: $critical-soft;
  }
}

/* Tabelas */
.td-table-card {
  overflow-x: auto;
}

.td-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th {
    padding: 0 12px 10px 0;
    border-bottom: 1px solid $grid;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    color: $ink-muted;
  }

  td {
    padding: 12px 12px 12px 0;
    border-bottom: 1px solid $grid;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  .is-number {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
}

.td-strong {
  font-weight: 600;
}

.td-muted {
  color: $ink-muted;
}

/* Gráficos */
.td-two-cols {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.td-chart-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 18px;
}

.td-chart-headline {
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
}

.td-goal-headline {
  font-weight: 400;

  strong {
    font-size: 22px;
  }
}

.td-chart-legend {
  font-size: 12px;
  color: $ink-muted;
}

.td-chart-note {
  margin-top: 14px !important;
  font-size: 12px;
  line-height: 16px;
  color: $ink-muted;
}

.td-bars {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: 180px;
  margin-top: 24px;
  border-bottom: 1px solid $grid;
}

.td-goal-line {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 2px dashed $ink-muted;
  z-index: 1;
  pointer-events: none;

  span {
    position: absolute;
    right: 0;
    top: -20px;
    padding: 0 4px;
    font-size: 11px;
    color: $ink-muted;
    background-color: #ffffff;
  }
}

.td-bar-slot {
  position: relative;
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
}

.td-bar {
  width: 60%;
  max-width: 36px;
  min-height: 2px;
  border-radius: 4px 4px 0 0;
  background-color: $primary;
  transition: opacity 0.15s ease-in-out;

  &.is-below {
    background-color: #b9b2d3;
  }

  &.is-hover {
    opacity: 0.8;
  }
}

.td-bar-label {
  position: absolute;
  font-size: 12px;
  font-weight: 700;
}

.td-bar-axis {
  display: flex;
  gap: 12px;
  margin-top: 6px;

  span {
    flex: 1;
    font-size: 12px;
    text-align: center;
    color: $ink-muted;
  }
}

/* Atividades do Art. 35: barras horizontais, um tom só */
.td-activities {
  margin: 0;
  padding: 0 !important;
  list-style: none;
}

.td-activity {
  display: grid;
  grid-template-columns: minmax(0, 190px) minmax(0, 1fr) 36px;
  align-items: center;
  gap: 12px;
  padding: 7px 0;
}

.td-activity-name {
  font-size: 13px;
  line-height: 16px;
}

.td-activity-track {
  height: 10px;
  border-radius: 5px;
  background-color: $primary-soft;
  overflow: hidden;
}

.td-activity-fill {
  display: block;
  height: 100%;
  border-radius: 5px;
  background-color: $primary;
}

.td-activity-value {
  font-size: 13px;
  font-weight: 700;
  text-align: right;
  font-variant-numeric: tabular-nums;

  &.is-zero {
    color: $ink-muted;
    font-weight: 400;
  }
}

@media (max-width: 1200px) {
  .td-two-cols {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
